import { Image } from 'antd';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectId, TaskId } from 'api/models';
import { ReactComponent as FormPlanDefaultImage } from 'assets/svgs/form-plan-default-image.svg';
import { Button } from 'components/atoms/button';
import { RoutePaths } from 'constant';
import { Task } from 'data/tasks';
import { useTasksByIds } from 'data/tasks/hooks';
import { setMarkerForDataDog } from 'hooks/use-data-dog-logger';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { TaskCardContent } from './task-card-content';
import { TaskCardFooter, TaskCardSubTasks } from './task-card-footer';
import { TaskCardHeader } from './task-card-header';
import {
  CardNew,
  CardNewContent,
  HeaderImageWrapper,
  ImageWrapper,
  NoResponseText,
  ViewResponseWrapper,
} from './task-card-new.styles';

interface CardProps {
  onClick: (e) => void;
  task: Task;
  showSubTasks: boolean;
  forExport: boolean;
  setShowSubTasks: React.Dispatch<React.SetStateAction<boolean>>;
  subTasksList: Task[];
  redirectToFormResponses: (e) => void;
}

const Card: FC<CardProps> = memo(
  ({
    onClick,
    task,
    showSubTasks,
    forExport,
    setShowSubTasks,
    subTasksList,
    redirectToFormResponses,
  }) => {
    return (
      <CardNew
        onClick={onClick}
        private={task.private}
        data-cy={task.taskName}
        data-private={task.private}
        bordered={false}
        hoverable
      >
        {task.formInstanceId ? (
          <HeaderImageWrapper>
            {task.formDetails?.headerImage ? (
              <ImageWrapper>
                <Image
                  preview={false}
                  alt={task.taskName}
                  src={task.formDetails.headerImage}
                  width="100%"
                  height={80}
                />
              </ImageWrapper>
            ) : (
              <FormPlanDefaultImage />
            )}
          </HeaderImageWrapper>
        ) : (
          ''
        )}
        <CardNewContent>
          <TaskCardHeader task={task} />

          <TaskCardContent task={task} />

          <TaskCardFooter
            task={task}
            subTaskVisible={showSubTasks}
            onSubTaskToggle={setShowSubTasks}
            subTasks={subTasksList}
          />

          {(forExport || showSubTasks) && subTasksList.length > 0 && (
            <TaskCardSubTasks task={task} subTasks={subTasksList} />
          )}

          {task.formInstanceId ? (
            <ViewResponseWrapper>
              {task.formDetails?.responseCount ? (
                <Button type="link" onClick={redirectToFormResponses}>
                  <FormattedMessage
                    id="forms.view_response.response_count"
                    values={{
                      count: task.formDetails.responseCount,
                    }}
                  />
                </Button>
              ) : (
                <NoResponseText>
                  <FormattedMessage id="forms.no_response" />
                </NoResponseText>
              )}
            </ViewResponseWrapper>
          ) : null}
        </CardNewContent>
      </CardNew>
    );
  },
);

export const TaskCardV2 = ({
  task,
  forExport = false,
  filterFunction,
}: {
  task: Task;
  forExport?: boolean;
  filterFunction: (task: Task) => boolean;
}) => {
  const subTasksList = useTasksByIds(ProjectId(task.project), task.subTasks);

  const filteredSubTasks = useMemo(
    () => (subTasksList ?? []).filter(filterFunction),
    [filterFunction, subTasksList],
  );

  const [showSubTasks, setShowSubTasks] = useState(false);
  const navigateToTask = useNavigateToTask();
  const openTask = useCallback(
    (taskId: number) => {
      navigateToTask(TaskId(task.project, taskId));
    },
    [navigateToTask, task.project],
  );

  const redirectToFormResponses = useCallback(
    (event) => {
      const formResponseUrl = RoutePaths.projectFormResponseByInstance
        .replace(':projectId', task.project.toString())
        .replace(':instanceId', task.formInstanceId?.toString() || '');

      window.open(formResponseUrl, '_blank');
      event.stopPropagation();
      event.preventDefault();
    },
    [task.formInstanceId, task.project],
  );

  const onClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      openTask(task.taskId);
      setMarkerForDataDog('project-plan:task-load');
    },
    [openTask, task.taskId],
  );

  return (
    <Card
      forExport={forExport}
      onClick={onClick}
      redirectToFormResponses={redirectToFormResponses}
      setShowSubTasks={setShowSubTasks}
      showSubTasks={showSubTasks}
      subTasksList={filteredSubTasks}
      task={task}
    />
  );
};
